import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const VAPID_PUBLIC_KEY = "BNB2mHfpI_gQU3HgPIgyJsBmJ4KEoHmnto0p4tQyxsgNbHiyZI0Zrb42tKjExuZAaPIi6CltfZrkwd7CLLA3CQo";

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushSubscription() {
  const { user } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>("default");

  const getServiceWorkerRegistration = useCallback(async () => {
    if (!("serviceWorker" in navigator)) return null;

    const registration = await navigator.serviceWorker.register("/sw-push.js");

    // Avoid waiting indefinitely for service worker activation on browsers
    // where ready can take too long on first install.
    const readyRegistration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<ServiceWorkerRegistration | null>((resolve) => {
        window.setTimeout(() => resolve(null), 5000);
      }),
    ]);

    return readyRegistration ?? registration;
  }, []);

  // Re-check permission on mount, focus, and visibility change
  useEffect(() => {
    const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setIsSupported(supported);
    if (!supported) return;

    const syncPermission = () => setPermission(Notification.permission);
    syncPermission();

    window.addEventListener("focus", syncPermission);
    document.addEventListener("visibilitychange", syncPermission);
    return () => {
      window.removeEventListener("focus", syncPermission);
      document.removeEventListener("visibilitychange", syncPermission);
    };
  }, []);

  // Check existing subscription on mount
  // Check if subscribed by verifying BOTH browser subscription AND database record
  useEffect(() => {
    if (!isSupported || !user) return;

    const checkSubscription = async () => {
      const registration = await getServiceWorkerRegistration();
      if (!registration) return;
      const browserSub = await registration.pushManager.getSubscription();
      
      if (!browserSub) {
        setIsSubscribed(false);
        return;
      }

      // Verify the subscription also exists in the database
      const { data, error } = await supabase
        .from("push_subscriptions")
        .select("id")
        .eq("user_id", user.id)
        .eq("endpoint", browserSub.endpoint)
        .maybeSingle();

      if (error || !data) {
        // Browser has a stale subscription - clean it up
        await browserSub.unsubscribe();
        setIsSubscribed(false);
      } else {
        setIsSubscribed(true);
      }
    };

    checkSubscription();
  }, [getServiceWorkerRegistration, isSupported, user]);

  const subscribe = useCallback(async () => {
    if (!isSupported || !user) {
      console.warn("[Push] Not supported or no user", { isSupported, user: !!user });
      return false;
    }

    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        console.warn("[Push] Permission not granted:", perm);
        return false;
      }

      const registration = await getServiceWorkerRegistration();
      if (!registration) return false;
      
      // Unsubscribe existing local subscription first
      const existing = await registration.pushManager.getSubscription();
      if (existing) {
        const existingEndpoint = existing.endpoint;
        await existing.unsubscribe();
        const { error: cleanupError } = await supabase
          .from("push_subscriptions")
          .delete()
          .eq("user_id", user.id)
          .eq("endpoint", existingEndpoint);
        if (cleanupError) {
          console.warn("[Push] Falha ao remover assinatura local antiga:", cleanupError);
        }
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const subJson = subscription.toJSON();

      // Keep subscriptions from other devices and deduplicate by current endpoint
      const { error } = await (supabase as any).from("push_subscriptions").upsert(
        {
          user_id: user.id,
          endpoint: subJson.endpoint!,
          p256dh: subJson.keys!.p256dh!,
          auth: subJson.keys!.auth!,
        },
        { onConflict: "user_id,endpoint" }
      );

      if (error) {
        console.error("[Push] Erro ao salvar subscrição:", error);
        return false;
      }

      setIsSubscribed(true);
      return true;
    } catch (err) {
      console.error("[Push] Erro ao subscrever:", err);
      return false;
    }
  }, [isSupported, user]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported || !user) return;

    try {
      const registration = await getServiceWorkerRegistration();
      if (!registration) return;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe();
        await supabase
          .from("push_subscriptions")
          .delete()
          .eq("user_id", user.id)
          .eq("endpoint", endpoint);
      }
      setIsSubscribed(false);
    } catch (err) {
      console.error("[Push] Erro ao cancelar subscrição:", err);
    }
  }, [getServiceWorkerRegistration, isSupported, user]);

  return { isSupported, isSubscribed, permission, subscribe, unsubscribe };
}
